/**
 * Your car on the map (docs/UI.md "Your car on the map"). The owner's complaint was "it's unclear where my car
 * is": after a walk round the block the car you left is just another dark rectangle. So the map carries two
 * cars of yours, each a car silhouette in its own paint on a paper plate (`kind: 'car'`, markerScheme.ts):
 *
 *  - **your motor** (glyph `★`): the car you keep — offline the Lockup 7 car (`lockup:car`) or the garage's
 *    "Keep this one" motor (`garage:motor`); online a server-owned car of yours (`ownedByPlayer`, set by the net
 *    module from the owner id). With several, the one you drove last, else the nearest.
 *  - **the last car you drove** (no glyph): the car you got out of, while it still exists — unless it IS your
 *    motor, or you are sitting in it.
 *
 * While you drive, that car is not a marker at all: the player arrow takes its paint (the marker and the arrow
 * merge). Pure — the UI system feeds it the vehicles each frame; tested in ownCar.test.ts.
 */
import { LOCKUP_CAR_TAG, MOTOR_TAG } from '../vehicles/types';
import type { MapMarker } from './uiTypes';

/** What the picker needs to know about a vehicle (a subset of vehicles/types.ts `Vehicle`). */
export interface CarLike {
  readonly id: number;
  readonly pos: { x: number; z: number };
  readonly paint: number;
  readonly destroyed: boolean;
  readonly ownedByPlayer: boolean;
  readonly tags: ReadonlySet<string>;
  readonly spec: { displayName: string };
}

export interface OwnCars<V extends CarLike = CarLike> {
  /** The car you keep (null: you own nothing yet). */
  motor: V | null;
  /** The car you last got out of, when it is not the motor and you are not in it. */
  last: V | null;
}

/** The car you keep: offline the tagged one, online one the server says is yours. */
export function isKeptCar(v: CarLike, online: boolean): boolean {
  if (v.destroyed) return false;
  return online ? v.ownedByPlayer : v.tags.has(LOCKUP_CAR_TAG) || v.tags.has(MOTOR_TAG);
}

/**
 * Pick your cars among `all`. `driving` is the car you sit in (excluded: it is the arrow), `lastDriven` the id
 * of the car you were in last (-1: none), `at` where you are (for "the nearest motor").
 */
export function pickOwnCars<V extends CarLike>(all: readonly V[], driving: V | null, lastDriven: number, online: boolean, at: { x: number; z: number }): OwnCars<V> {
  let motor: V | null = null;
  let motorD = Infinity;
  let last: V | null = null;
  for (const v of all) {
    if (v.destroyed) continue;
    if (v.id === lastDriven && v !== driving) last = v;
    if (!isKeptCar(v, online)) continue;
    // The motor you drove last wins; otherwise the nearest.
    const d = v.id === lastDriven ? -1 : (v.pos.x - at.x) ** 2 + (v.pos.z - at.z) ** 2;
    if (d < motorD) {
      motorD = d;
      motor = v;
    }
  }
  if (motor === driving) motor = null;
  if (last === motor) last = null;
  return { motor, last };
}

/** A paint number (0xrrggbb) as a CSS colour. */
export function paintCss(paint: number): string {
  return `#${(paint & 0xffffff).toString(16).padStart(6, '0')}`;
}

/** Push the map markers for your cars: the motor (glyph ★, pinned to the rim) and the last car you drove. */
export function pushCarMarkers(cars: OwnCars, out: MapMarker[]): void {
  if (cars.motor) {
    const v = cars.motor;
    out.push({ x: v.pos.x, z: v.pos.z, kind: 'car', glyph: '★', pin: true, tint: paintCss(v.paint), label: `Your motor — ${v.spec.displayName}` });
  }
  if (cars.last) {
    const v = cars.last;
    out.push({ x: v.pos.x, z: v.pos.z, kind: 'car', pin: true, tint: paintCss(v.paint), label: `Your last car — ${v.spec.displayName}` });
  }
}

/** The car marker to find first: your motor, else the last car you drove. */
export function findCar(markers: readonly MapMarker[]): MapMarker | null {
  let best: MapMarker | null = null;
  for (const m of markers) {
    if (m.kind !== 'car' || m.area) continue;
    if (m.glyph === '★') return m;
    best ??= m;
  }
  return best;
}

/** "120 m" / "1.4 km": a distance for the rim and the Find my car button (rounded so it doesn't flicker). */
export function distanceText(metres: number): string {
  if (metres < 1000) return `${Math.max(10, Math.round(metres / 10) * 10)} m`;
  return `${(metres / 1000).toFixed(1)} km`;
}
